// Shared pure helpers for the Data QA page: grain and gate labels, path and
// status classification. The check copy and the row's Readiness verdict live in
// data-qa-copy, so the table and the drill-down classify the same way.
import { capitalize } from 'inflected';
import {
  AnalysisPath,
  DataQaGroup,
  DataQaSeries,
  DataQaThresholds,
  FailReason,
  ForecastLadderRung,
  IdentifiabilityFlag
} from '../dataQaContract';

// ---- display copy --------------------------------------------------------------

export const grainLabel = (grain: string): string => {
  if (grain === 'finest') {
    return 'Full granularity';
  }
  if (grain === 'none') {
    return 'No viable path';
  }
  return grain.split('_').map(capitalize).join(' × ');
};

export const failCopy = (code: FailReason, t: DataQaThresholds): string => {
  switch (code) {
    case 'sample_size':
      return `Less than ${t.forecast_min_days} days of usable daily history`;
    case 'dark_days':
      return 'Spend gaps exceed the allowed dark-day limits';
    case 'weekly_sample_size':
      return `Fewer than ${t.scaling_min_weeks} complete weeks of history`;
    default:
      return code;
  }
};

// ---- identifiability -------------------------------------------------------------

// Worst identifiability flag across the group's pairings. Only the two bad
// flags count (they feed the row's Readiness verdict in data-qa-copy): the type
// also permits identifiable/identified_noisy, which are fine outcomes.
const IDENT_RANK: Record<IdentifiabilityFlag, number> = {
  identifiable: 0,
  identified_noisy: 1,
  caution: 2,
  at_risk: 3
};

export const identifiabilityFlag = (g: DataQaGroup): 'caution' | 'at_risk' | null => {
  const worst = g.warnings.reduce<IdentifiabilityFlag | null>((acc, w) => {
    if (w.code !== 'identifiability' || !w.flag) {
      return acc;
    }
    return acc === null || IDENT_RANK[w.flag] > IDENT_RANK[acc] ? w.flag : acc;
  }, null);
  return worst === 'caution' || worst === 'at_risk' ? worst : null;
};

// ---- forecast-grain classification ----------------------------------------------

// This page's own "is the finest node ready" classification (independent of
// which rungs Request Forecast actually offers — see request-form-utils.ts,
// which additionally applies suppression rules on top of any-rung viability).
const finestRung = (path: AnalysisPath<ForecastLadderRung>): ForecastLadderRung | undefined =>
  path.ladder.find(rung => rung.grain === 'finest');

export const isReadyAtFinest = (path: AnalysisPath<ForecastLadderRung>): boolean => {
  const finest = finestRung(path);
  return Boolean(finest?.offered && finest.eligible);
};

// ---- status classification -----------------------------------------------------

// Single source of truth for the status decision — the plain-text label (sort
// key) and the chip both classify through this so the two can never drift.
type StatusKind = 'ready' | 'rollup' | 'none';

export const statusKind = (
  eligible: boolean,
  recommendedGrain: string,
  downgraded: boolean
): StatusKind => {
  if (eligible) {
    return 'ready';
  }
  if (downgraded && recommendedGrain !== 'none') {
    return 'rollup';
  }
  return 'none';
};

// Plain-text form of the status chip — reused as the column's sort key so
// header-click sorting orders rows the same way the chips read.
export const statusText = (
  eligible: boolean,
  recommendedGrain: string,
  downgraded: boolean
): string => {
  switch (statusKind(eligible, recommendedGrain, downgraded)) {
    case 'ready':
      return 'Ready';
    case 'rollup':
      return `Via ${grainLabel(recommendedGrain)}`;
    default:
      return 'No path';
  }
};

// ---- lazy weekly-series fetch state --------------------------------------------

export type SeriesState =
  | { status: 'idle' }
  | { status: 'loading' }
  | { status: 'error' }
  | { status: 'ready'; data: DataQaSeries };
